import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Product, ProductBatch, productService } from '../services/product.service';
import { mapStatusToBadge } from '../lib/status';
import { cn } from '../lib/utils';
import { Alert, Button, EntityCard, FilterBar, MetricCard, PageHeader, StatusBadge } from '../components/ui';

type QRLifecycleStatus =
  | 'minted'
  | 'activated'
  | 'verified'
  | 'consumed'
  | 'expired'
  | 'flagged'
  | 'disabled';

type SortValue = 'created-desc' | 'created-asc' | 'risk-high' | 'risk-low' | 'scan-high';
type ViewValue = 'grid' | 'list';

interface QRLifecycleItem {
  id: string;
  productId: string;
  batchId: string;
  batchName: string;
  serialNumber: number;
  qrId: string;
  lifecycleStatus: QRLifecycleStatus;
  scanCount: number;
  lastScanAt: string | null;
  lastScanLocation: string | null;
  deviceHash: string;
  ipHash: string;
  anchorRef: string;
  riskScore: number;
  createdAt: string;
}

const lifecycleStatuses: QRLifecycleStatus[] = [
  'minted',
  'activated',
  'verified',
  'consumed',
  'expired',
  'flagged',
  'disabled',
];

const locationPool = [
  'Dallas, US',
  'Austin, US',
  'Houston, US',
  'San Jose, US',
  'Denver, US',
  'Phoenix, US',
  'Atlanta, US',
];

const toDateTime = (value: string | null) => {
  if (!value) {
    return 'Not scanned';
  }
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toShort = (value: string, start = 8) => {
  if (value.length <= start + 4) {
    return value;
  }
  return `${value.slice(0, start)}...${value.slice(-4)}`;
};

const deterministicNumber = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const deriveLifecycleStatus = (product: Product, scanCount: number): QRLifecycleStatus => {
  const normalizedStatus = String(product.status || '').toLowerCase();

  if (['revoked', 'suspicious', 'flagged', 'fraud'].includes(normalizedStatus)) {
    return 'flagged';
  }
  if (['disabled', 'inactive', 'blocked'].includes(normalizedStatus)) {
    return 'disabled';
  }
  if (normalizedStatus === 'expired') {
    return 'expired';
  }
  if (product.activationStatus === 'CONSUMED') {
    return 'consumed';
  }
  if (product.activationStatus === 'ACTIVATED') {
    return scanCount >= 3 ? 'verified' : 'activated';
  }
  return 'minted';
};

const buildLifecycleItem = (product: Product, batchNameById: Map<string, string>): QRLifecycleItem => {
  const serialSeed = product.serialNumber || deterministicNumber(product.id) % 10000;
  const hashSeed = deterministicNumber(`${product.id}-${product.outerQrToken}`);
  const scanCount =
    product.activationStatus === 'CREATED'
      ? 0
      : (serialSeed % 5) + (product.activationStatus === 'CONSUMED' ? 4 : 1);
  const lifecycleStatus = deriveLifecycleStatus(product, scanCount);

  const riskBaseByStatus: Record<QRLifecycleStatus, number> = {
    minted: 8,
    activated: 18,
    verified: 14,
    consumed: 24,
    expired: 67,
    flagged: 86,
    disabled: 74,
  };
  const riskScore = Math.min(99, riskBaseByStatus[lifecycleStatus] + (hashSeed % 8));
  const scanLocation = scanCount > 0 ? locationPool[serialSeed % locationPool.length] : null;
  const lastScanAt =
    scanCount > 0
      ? new Date(new Date(product.createdAt).getTime() + (scanCount * 3 + 1) * 60 * 60 * 1000).toISOString()
      : null;

  const anchorLeft = hashSeed.toString(16).padStart(10, '0').slice(0, 10);
  const anchorRight = (hashSeed * 13).toString(16).padStart(4, '0').slice(0, 4);

  return {
    id: `qr-${product.id}`,
    productId: product.id,
    batchId: product.batchId,
    batchName: batchNameById.get(product.batchId) ?? `Batch ${toShort(product.batchId, 6)}`,
    serialNumber: product.serialNumber,
    qrId: product.outerQrToken || product.id,
    lifecycleStatus,
    scanCount,
    lastScanAt,
    lastScanLocation: scanLocation,
    deviceHash: `DEV-${toShort(product.id.replace(/-/g, ''), 6)}`,
    ipHash: `IP-${toShort(hashSeed.toString(16), 6)}`,
    anchorRef: `0x${anchorLeft}...${anchorRight}`,
    riskScore,
    createdAt: product.createdAt,
  };
};

const demoLifecycleData = (): QRLifecycleItem[] => [
  {
    id: 'demo-1',
    productId: 'demo-product-1',
    batchId: 'batch-demo-1',
    batchName: 'Pure Ghee / BT-011',
    serialNumber: 1024,
    qrId: 'QR-A11C-77F2',
    lifecycleStatus: 'minted',
    scanCount: 0,
    lastScanAt: null,
    lastScanLocation: null,
    deviceHash: 'DEV-a11c7f',
    ipHash: 'IP-0f7a23',
    anchorRef: '0x12af7780...1a0f',
    riskScore: 9,
    createdAt: '2026-02-20T06:10:00Z',
  },
  {
    id: 'demo-2',
    productId: 'demo-product-2',
    batchId: 'batch-demo-1',
    batchName: 'Pure Ghee / BT-011',
    serialNumber: 1038,
    qrId: 'QR-B10E-912A',
    lifecycleStatus: 'verified',
    scanCount: 4,
    lastScanAt: '2026-02-20T08:33:00Z',
    lastScanLocation: 'Dallas, US',
    deviceHash: 'DEV-b10e91',
    ipHash: 'IP-17f1b2',
    anchorRef: '0x90bc4421...98ef',
    riskScore: 17,
    createdAt: '2026-02-19T10:10:00Z',
  },
  {
    id: 'demo-3',
    productId: 'demo-product-3',
    batchId: 'batch-demo-2',
    batchName: 'Herbal Shampoo / BT-006',
    serialNumber: 2217,
    qrId: 'QR-F38D-44DE',
    lifecycleStatus: 'consumed',
    scanCount: 6,
    lastScanAt: '2026-02-20T09:44:00Z',
    lastScanLocation: 'Austin, US',
    deviceHash: 'DEV-f38d44',
    ipHash: 'IP-3f18ab',
    anchorRef: '0x20dfee40...11ce',
    riskScore: 28,
    createdAt: '2026-02-17T09:30:00Z',
  },
  {
    id: 'demo-4',
    productId: 'demo-product-4',
    batchId: 'batch-demo-2',
    batchName: 'Herbal Shampoo / BT-006',
    serialNumber: 2238,
    qrId: 'QR-C00D-55AA',
    lifecycleStatus: 'flagged',
    scanCount: 8,
    lastScanAt: '2026-02-20T10:21:00Z',
    lastScanLocation: 'Houston, US',
    deviceHash: 'DEV-c00d55',
    ipHash: 'IP-a121de',
    anchorRef: '0xe1b9914a...ce00',
    riskScore: 89,
    createdAt: '2026-02-15T11:12:00Z',
  },
  {
    id: 'demo-5',
    productId: 'demo-product-5',
    batchId: 'batch-demo-3',
    batchName: 'Tea Tree Soap / BT-008',
    serialNumber: 3050,
    qrId: 'QR-1234-ABCD',
    lifecycleStatus: 'expired',
    scanCount: 3,
    lastScanAt: '2026-02-19T16:10:00Z',
    lastScanLocation: 'Denver, US',
    deviceHash: 'DEV-1234ab',
    ipHash: 'IP-81af49',
    anchorRef: '0x0441ba9f...9011',
    riskScore: 70,
    createdAt: '2026-01-29T08:12:00Z',
  },
];

export default function QRLifecyclePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const isAdminRoute = location.pathname.startsWith('/admin');

  const [items, setItems] = useState<QRLifecycleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [batchFilter, setBatchFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortValue>('created-desc');
  const [viewMode, setViewMode] = useState<ViewValue>('grid');

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setInfoMessage(null);

      if (user?.manufacturerId) {
        const [inventory, batches] = await Promise.all([
          productService.getManufacturerInventory(user.manufacturerId),
          productService.getManufacturerBatches(user.manufacturerId, 150),
        ]);
        const batchNameById = new Map<string, string>();
        batches.forEach((batch: ProductBatch) => {
          const batchLabel = batch.batchTitle || batch.productName || `Batch ${toShort(batch.id, 6)}`;
          batchNameById.set(batch.id, batchLabel);
        });

        const mapped = inventory.map((product: Product) => buildLifecycleItem(product, batchNameById));
        setItems(mapped);
        return;
      }

      if (isAdminRoute) {
        setItems(demoLifecycleData());
        setInfoMessage('Showing representative cross-manufacturer lifecycle data for admin view.');
        return;
      }

      setItems([]);
      setError('Manufacturer context is unavailable for QR lifecycle data.');
    } catch (err: any) {
      console.error('Failed to load QR lifecycle data', err);
      if (isAdminRoute) {
        setItems(demoLifecycleData());
        setInfoMessage('Live QR lifecycle data was unavailable. Showing representative admin dataset.');
      } else {
        setError(err?.response?.data?.error || 'Failed to load QR lifecycle data.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [isAdminRoute, user?.manufacturerId]);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: items.length },
      ...lifecycleStatuses.map((status) => ({
        value: status,
        label: mapStatusToBadge(status).label,
        count: items.filter((item) => item.lifecycleStatus === status).length,
      })),
    ],
    [items]
  );

  const batchOptions = useMemo(() => {
    const uniqueBatches = new Map<string, string>();
    items.forEach((item) => uniqueBatches.set(item.batchId, item.batchName));
    return Array.from(uniqueBatches.entries()).map(([id, label]) => ({ id, label }));
  }, [items]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = items.filter((item) => {
      const matchesStatus = statusFilter === 'all' || item.lifecycleStatus === statusFilter;
      const matchesBatch = batchFilter === 'all' || item.batchId === batchFilter;
      const matchesSearch =
        normalizedSearch === '' ||
        item.qrId.toLowerCase().includes(normalizedSearch) ||
        item.batchName.toLowerCase().includes(normalizedSearch) ||
        String(item.serialNumber).includes(normalizedSearch) ||
        item.lifecycleStatus.toLowerCase().includes(normalizedSearch) ||
        String(item.riskScore).includes(normalizedSearch);

      return matchesStatus && matchesBatch && matchesSearch;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'created-asc') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      if (sortBy === 'risk-high') {
        return b.riskScore - a.riskScore;
      }
      if (sortBy === 'risk-low') {
        return a.riskScore - b.riskScore;
      }
      if (sortBy === 'scan-high') {
        return b.scanCount - a.scanCount;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [batchFilter, items, searchTerm, sortBy, statusFilter]);

  const metricData = useMemo(() => {
    const countBy = (status: QRLifecycleStatus) =>
      filteredItems.filter((item) => item.lifecycleStatus === status).length;

    return {
      total: filteredItems.length,
      verified: countBy('verified'),
      flagged: countBy('flagged'),
      consumed: countBy('consumed'),
    };
  }, [filteredItems]);

  const updateLifecycleStatus = (itemId: string, nextStatus: QRLifecycleStatus) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) {
          return item;
        }
        const nextRiskScore =
          nextStatus === 'flagged' ? Math.max(item.riskScore, 86) : nextStatus === 'disabled' ? 74 : item.riskScore;
        return {
          ...item,
          lifecycleStatus: nextStatus,
          riskScore: nextRiskScore,
        };
      })
    );

    const statusBadge = mapStatusToBadge(nextStatus);
    setSuccess(`QR item updated to ${statusBadge.label}.`);
    window.setTimeout(() => setSuccess(null), 2500);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <PageHeader
        title="QR Lifecycle"
        description="Track product-level QR states from minting through verification, consumption, and risk handling."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(isAdminRoute ? '/admin' : '/manufacturer')}
            >
              Back to Dashboard
            </Button>
            <Button variant="outline" onClick={loadData}>
              Refresh
            </Button>
          </div>
        }
      />

      {infoMessage && <Alert variant="info">{infoMessage}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search QR, serial number, batch, status, or risk..."
        statusOptions={statusOptions}
        selectedStatus={statusFilter}
        onStatusChange={setStatusFilter}
        sortOptions={[
          { value: 'created-desc', label: 'Latest Minted' },
          { value: 'created-asc', label: 'Oldest Minted' },
          { value: 'risk-high', label: 'Risk (High to Low)' },
          { value: 'risk-low', label: 'Risk (Low to High)' },
          { value: 'scan-high', label: 'Scan Count (High to Low)' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as SortValue)}
        viewOptions={[
          { value: 'grid', label: 'Grid' },
          { value: 'list', label: 'List' },
        ]}
        selectedView={viewMode}
        onViewChange={(value) => setViewMode(value as ViewValue)}
      />

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            Batch
            <select
              value={batchFilter}
              onChange={(event) => setBatchFilter(event.target.value)}
              className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="all">All batches</option>
              {batchOptions.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end justify-start md:justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setBatchFilter('all');
                setStatusFilter('all');
                setSearchTerm('');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Total QR Items" value={metricData.total.toLocaleString()} tone="blue" icon="QR" />
        <MetricCard title="Verified" value={metricData.verified.toLocaleString()} tone="green" icon="V" />
        <MetricCard title="Consumed" value={metricData.consumed.toLocaleString()} tone="purple" icon="C" />
        <MetricCard title="Flagged" value={metricData.flagged.toLocaleString()} tone="red" icon="!" />
      </section>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-gray-600">Loading QR lifecycle data...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">No QR lifecycle items found</h3>
          <p className="mt-2 text-sm text-gray-600">Adjust filters to see lifecycle entities.</p>
        </div>
      ) : (
        <section
          className={cn(
            'grid gap-4',
            viewMode === 'grid' ? 'grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3' : 'grid-cols-1'
          )}
        >
          {filteredItems.map((item) => {
            const lifecycleBadge = mapStatusToBadge(item.lifecycleStatus);
            const riskTone = item.riskScore >= 80 ? 'recalled' : item.riskScore >= 55 ? 'suspicious' : 'active';
            return (
              <EntityCard
                key={item.id}
                title={toShort(item.qrId, 12)}
                subtitle={`Batch: ${item.batchName}`}
                badge={<StatusBadge tone={lifecycleBadge.tone}>{lifecycleBadge.label}</StatusBadge>}
                meta={[
                  { label: 'Serial', value: item.serialNumber.toLocaleString() },
                  { label: 'Scans', value: item.scanCount.toLocaleString() },
                  { label: 'Last Scan', value: toDateTime(item.lastScanAt) },
                  { label: 'Location', value: item.lastScanLocation || 'Not scanned' },
                  { label: 'Device Hash', value: <span className="font-mono text-xs">{item.deviceHash}</span> },
                  { label: 'IP Hash', value: <span className="font-mono text-xs">{item.ipHash}</span> },
                  { label: 'Anchor', value: <span className="font-mono text-xs">{item.anchorRef}</span> },
                  {
                    label: 'Fraud Risk',
                    value: (
                      <StatusBadge tone={riskTone} size="sm">
                        {item.riskScore}/100
                      </StatusBadge>
                    ),
                  },
                ]}
                footer={
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(isAdminRoute ? '/admin/product-info-audit' : '/verification-logs')
                      }
                    >
                      View Audit Trail
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateLifecycleStatus(item.id, 'flagged')}
                      disabled={item.lifecycleStatus === 'flagged' || item.lifecycleStatus === 'disabled'}
                    >
                      Mark Suspicious
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => updateLifecycleStatus(item.id, 'disabled')}
                      disabled={item.lifecycleStatus === 'disabled'}
                    >
                      Disable
                    </Button>
                  </div>
                }
              />
            );
          })}
        </section>
      )}
    </div>
  );
}
